import React, { useState, useEffect } from 'react';
import TOUModal from './TOUModal';
import touService from '../../services/touService';
import { useAuth } from '../../context/AuthContext';

/**
 * TOUGuard - Ensures users have accepted current TOU before accessing content
 * This component wraps protected content and shows TOU modal if needed
 */
const TOUGuard = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [touStatus, setTouStatus] = useState(null);
  const [touDocument, setTouDocument] = useState(null);
  const [privacyDocument, setPrivacyDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      checkTOUStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const checkTOUStatus = async () => {
    try {
      setLoading(true);
      
      // Check if user needs to accept TOU
      const status = await touService.getTOUStatus();
      setTouStatus(status);

      if (status.needsAcceptance) {
        // Load documents if needed
        const [touDoc, privacyDoc] = await Promise.all([
          touService.getTOUDocument(),
          touService.getPrivacyDocument()
        ]);
        setTouDocument(touDoc);
        setPrivacyDocument(privacyDoc);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error checking TOU status:', error);
      // If we can't check status, allow access (fail open)
      // This prevents users from being locked out due to server issues
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setAccepting(true);
      await touService.acceptTOU();
      setShowModal(false);
      setTouStatus({ ...touStatus, needsAcceptance: false });
    } catch (error) {
      console.error('Error accepting TOU:', error);
      alert('Failed to accept terms. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Show loading state while checking TOU
  if (authLoading || loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a0a2e 0%, #0f051d 100%)',
        color: '#c9a962',
        fontFamily: 'Cinzel, serif',
        fontSize: '1.5rem'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* Show TOU modal if user needs to accept */}
      {showModal && (
        <TOUModal
          isOpen={showModal}
          onAccept={handleAccept}
          touContent={touDocument}
          privacyContent={privacyDocument}
          accepting={accepting}
        />
      )}
      
      {/* Show children only if TOU is accepted or not required */}
      {!touStatus?.needsAcceptance && children}
    </>
  );
};

export default TOUGuard;
